<template>
  <div class="agents-container">
    <!-- Top Header -->
    <div class="page-header">
      <h1>Agents</h1>
      <button 
        v-if="!showCreateForm" 
        @click="showCreateForm = true" 
        class="btn btn-primary"
      >
        <i class="icon-plus"></i> Create Agent
      </button>
    </div>
    
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>
    
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel: Agents List -->
      <div class="left-panel">
        <div class="panel-header">
          <h3>Agents</h3>
          <span class="agent-count">{{ filteredAgents.length }} agents</span>
        </div>
        <div class="list-toolbar">
          <div class="search-box">
            <i class="icon-search"></i>
            <input v-model="agentSearch" placeholder="Search agents...">
          </div>
        </div>
        
        <div v-if="agents.length === 0" class="no-data">
          <div class="empty-state">
            <i class="icon-agent"></i>
            <h4>No agents yet</h4>
            <p>Create your first agent to get started</p>
          </div>
        </div>
        
        <div v-else class="agents-list">
          <div 
            v-for="agent in filteredAgents" 
            :key="agent.id" 
            class="agent-item"
            :class="{ 'active': selectedAgent?.id === agent.id }"
            @click="selectAgent(agent)"
          >
            <div class="agent-avatar">
              <i class="icon-bot"></i>
            </div>
            <div class="agent-info">
              <h4>{{ agent.name }}</h4>
              <p>{{ agent.description }}</p>
              <div class="agent-meta">
                <span class="agent-type">{{ agent.type || 'chat' }}</span>
                <span v-if="!agent.is_active" class="agent-status inactive">Inactive</span>
              </div>
            </div>
            <div class="agent-actions">
              <button @click.stop="editAgent(agent)" class="btn-icon" title="Edit">
                <i class="icon-edit"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Panel: Agent Details/Form/Chat -->
      <div class="right-panel">
        <!-- Create/Edit Agent Form -->
        <div v-if="showCreateForm || editingAgent" class="agent-form-panel">
          <div class="panel-header">
            <h3>{{ editingAgent ? 'Edit Agent' : 'Create New Agent' }}</h3>
            <button @click="closeForm" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="editingAgent ? updateAgent() : createAgent()" class="agent-form">
            <div class="form-row">
              <div class="form-group">
                <label>Agent Name *</label>
                <input
                  v-model="agentForm.name"
                  type="text"
                  placeholder="Enter agent name"
                  required
                >
              </div>
              <div class="form-group">
                <label>Agent Type *</label>
                <select v-model="agentForm.type" required>
                  <option value="chat">Chat Agent</option>
                  <option value="rag">RAG Agent</option>
                  <option value="workflow">Workflow Agent</option>
                  <option value="api">API Agent</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Description *</label>
              <textarea
                v-model="agentForm.description"
                placeholder="Describe what this agent does"
                required
                rows="3"
              ></textarea>
            </div>
            
            <div class="form-group">
              <label>LLM Model *</label>
              <select v-model="agentForm.llm_model_id" required>
                <option value="">Select a model</option>
                <option v-for="model in models" :key="model.id" :value="model.id">
                  {{ model.name }} ({{ model.provider }})
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>System Prompt</label>
              <textarea
                v-model="agentForm.system_prompt"
                placeholder="System instructions for the agent..."
                rows="4"
              ></textarea>
            </div>
            
            <!-- Advanced Settings -->
            <div class="settings-section">
              <h4>Settings</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Temperature ({{ agentForm.settings.temperature }})</label>
                  <input 
                    type="range" 
                    v-model.number="agentForm.settings.temperature" 
                    min="0" 
                    max="1" 
                    step="0.1"
                    class="range-input"
                  >
                </div>
                <div class="form-group">
                  <label>Max Tokens</label>
                  <input 
                    type="number" 
                    v-model.number="agentForm.settings.max_tokens" 
                    min="100" 
                    max="8000"
                    placeholder="1000"
                  >
                </div>
              </div>
              
              <div v-if="agentForm.type === 'rag'" class="form-row">
                <div class="form-group">
                  <label>Top K Results</label>
                  <input 
                    type="number" 
                    v-model.number="agentForm.settings.top_k" 
                    min="1" 
                    max="20"
                    placeholder="5"
                  >
                </div>
                <div class="form-group">
                  <label>Similarity Threshold ({{ agentForm.settings.similarity_threshold }})</label>
                  <input 
                    type="range" 
                    v-model.number="agentForm.settings.similarity_threshold" 
                    min="0.1" 
                    max="1" 
                    step="0.05"
                    class="range-input"
                  >
                </div>
              </div>
              
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="agentForm.settings.enable_memory">
                  <span>Enable Memory</span>
                </label>
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeForm" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingAgent ? 'Update Agent' : 'Create Agent' }}
              </button>
            </div>
          </form>
        </div>
        
        <!-- Agent Details & Chat -->
        <div v-else-if="selectedAgent" class="agent-details-panel">
          <div class="panel-header agent-header">
            <div class="agent-title">
              <h3>{{ selectedAgent.name }}</h3>
              <span class="agent-type-badge">{{ selectedAgent.type || 'chat' }}</span>
              <span v-if="getModelName(selectedAgent.llm_model_id) !== 'Unknown Model'" class="model-chip">
                {{ getModelName(selectedAgent.llm_model_id) }}
              </span>
              <span v-if="selectedAgent.type === 'rag'" class="rag-chip"><i class="icon-knowledge"></i> RAG</span>
            </div>
            <div class="header-actions">
              <label class="switch" title="Toggle active status">
                <input type="checkbox" :checked="!!selectedAgent.is_active" @change="toggleAgentStatus">
                <span class="slider"></span>
              </label>
              <button @click="activeTab = 'chat'" class="btn btn-outline">
                <i class="icon-chat"></i> Test Chat
              </button>
              <button @click="editAgent(selectedAgent)" class="btn btn-outline">
                <i class="icon-edit"></i> Edit
              </button>
            </div>
          </div>
          
          <!-- Tab Navigation -->
          <div class="tab-navigation">
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'overview' }"
              @click="activeTab = 'overview'"
            >
              <i class="icon-info"></i> Overview
            </button>
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'chat' }"
              @click="activeTab = 'chat'"
            >
              <i class="icon-chat"></i> Chat
            </button>
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'conversations' }"
              @click="activeTab = 'conversations'"
            >
              <i class="icon-history"></i> Conversations
            </button>
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'memory' }"
              @click="activeTab = 'memory'"
            >
              <i class="icon-memory"></i> Memory
            </button>
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'tools' }"
              @click="activeTab = 'tools'"
            >
              🧰 Tools
            </button>
          </div>
          
          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Conversations Tab -->
            <div v-if="activeTab === 'conversations'" class="tab-panel">
              <div class="conversations-section">
                <div class="conversations-header">
                  <h4>Chat History</h4>
                  <div class="convo-actions">
                    <div class="search-box">
                      <i class="icon-search"></i>
                      <input v-model="conversationSearch" type="text" placeholder="Search conversations...">
                    </div>
                    <button @click="refreshConversations" class="btn btn-outline btn-sm">
                      <i class="icon-refresh"></i> Refresh
                    </button>
                  </div>
                </div>
                
                <div v-if="conversationsLoading" class="loading-state">
                  <i class="icon-loading"></i>
                  <p>Loading conversations...</p>
                </div>
                
                <div v-else-if="conversations.length === 0" class="empty-state">
                  <i class="icon-chat"></i>
                  <h4>No conversations yet</h4>
                  <p>Start a conversation with this agent to see chat history here</p>
                </div>
                
                <div v-else class="conversations-list">
                  <div 
                    v-for="conversation in filteredConversations" 
                    :key="conversation.id"
                    class="conversation-item"
                    :class="{ 'active': selectedConversation?.id === conversation.id }"
                    @click="selectConversation(conversation)"
                  >
                    <div class="conversation-header">
                      <div class="conversation-info">
                        <h5>{{ formatConversationTitle(conversation) }}</h5>
                        <span class="conversation-time">{{ formatDateTime(conversation.created_at) }}</span>
                      </div>
                      <div class="conversation-stats">
                        <span class="message-count">{{ conversation.message_count || 0 }} messages</span>
                        <span class="conversation-status" :class="conversation.status">{{ conversation.status || 'active' }}</span>
                      </div>
                    </div>
                    <div class="conversation-preview">
                      <p>{{ conversation.last_message || 'No messages yet' }}</p>
                    </div>
                  </div>
                </div>
                
                <!-- Selected Conversation Messages -->
                <div v-if="selectedConversation" class="conversation-messages">
                  <div class="messages-header">
                    <h4>Messages</h4>
                    <div class="message-actions">
                      <button @click="exportConversation" class="btn btn-outline btn-sm">
                        <i class="icon-export"></i> Export
                      </button>
                      <button @click="deleteConversation" class="btn btn-danger btn-sm">
                        <i class="icon-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                  
                  <div v-if="conversationMessagesLoading" class="loading-state">
                    <i class="icon-loading"></i>
                    <p>Loading messages...</p>
                  </div>
                  
                  <div v-else class="messages-container">
                    <div 
                      v-for="message in conversationMessages" 
                      :key="message.id"
                      class="message-item"
                      :class="message.role"
                    >
                      <div class="message-header">
                        <span class="message-role">{{ message.role }}</span>
                        <span class="message-time">{{ formatDateTime(message.created_at) }}</span>
                      </div>
                      <div class="message-content">
                        {{ message.content }}
                      </div>
                      <div v-if="message.model_used" class="message-meta">
                        <span class="model-info">Model: {{ message.model_used }}</span>
                        <span v-if="message.tokens_used" class="tokens-info">Tokens: {{ message.tokens_used }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Overview Tab -->
            <div v-if="activeTab === 'overview'" class="tab-panel">
              <div class="agent-overview">
                <p class="agent-description">{{ selectedAgent.description }}</p>
                <div class="agent-details">
                  <div class="detail-item">
                    <span class="label">Model:</span>
                    <span class="value">{{ getModelName(selectedAgent.llm_model_id) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Status:</span>
                    <span class="value status" :class="{ 'active': selectedAgent.is_active }">
                      {{ selectedAgent.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Memory:</span>
                    <span class="value">{{ selectedAgent.settings?.enable_memory ? 'Enabled' : 'Disabled' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">Type:</span>
                    <span class="value">{{ selectedAgent.type || 'chat' }}</span>
                  </div>
                </div>
                
                <!-- System Prompt -->
                <div v-if="selectedAgent.system_prompt" class="system-prompt-section">
                  <h4>System Prompt</h4>
                  <div class="system-prompt">{{ selectedAgent.system_prompt }}</div>
                </div>
                
                <!-- Settings -->
                <div v-if="selectedAgent.settings" class="settings-overview">
                  <h4>Configuration</h4>
                  <div class="settings-grid">
                    <div class="setting-item">
                      <span class="setting-label">Temperature:</span>
                      <span class="setting-value">{{ selectedAgent.settings.temperature || 0.7 }}</span>
                    </div>
                    <div class="setting-item">
                      <span class="setting-label">Max Tokens:</span>
                      <span class="setting-value">{{ selectedAgent.settings.max_tokens || 1000 }}</span>
                    </div>
                    <div v-if="selectedAgent.type === 'rag'" class="setting-item">
                      <span class="setting-label">Top K:</span>
                      <span class="setting-value">{{ selectedAgent.settings.top_k || 5 }}</span>
                    </div>
                    <div v-if="selectedAgent.type === 'rag'" class="setting-item">
                      <span class="setting-label">Similarity Threshold:</span>
                      <span class="setting-value">{{ selectedAgent.settings.similarity_threshold || 0.7 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Memory Tab -->
            <div v-if="activeTab === 'memory'" class="tab-panel">
              <AgentMemoryManager 
                :agent="selectedAgent" 
                @upload-success="onMemoryUploadSuccess"
                @error="onMemoryError"
              />
            </div>
            
            <!-- Chat Tab -->
            <div v-if="activeTab === 'chat'" class="tab-panel">
              <!-- Chat Interface -->
          <div class="chat-section">
            <div class="chat-header">
              <h4>Chat with {{ selectedAgent.name }}</h4>
              <div v-if="selectedAgent.type === 'rag'" class="rag-indicator">
                <i class="icon-knowledge"></i> RAG Enabled
              </div>
            </div>
            
            <!-- Model Selection -->
            <div class="model-selector">
              <select v-model="selectedModel" required>
                <option value="">Choose a model for this chat</option>
                <option v-for="model in models" :key="model.id" :value="model.id">
                  {{ model.name }} ({{ model.provider }})
                </option>
              </select>
            </div>
            
            <!-- Chat Messages -->
            <div class="chat-messages" ref="chatMessages">
              <div v-if="chatHistory.length === 0" class="no-messages">
                <i class="icon-chat"></i>
                <p>Start a conversation with {{ selectedAgent.name }}</p>
              </div>
              <div v-for="(message, index) in chatHistory" :key="index" 
                :class="['message', message.type]">
                <div class="message-content">
                  {{ message.text }}
                </div>
                <div class="message-time">
                  {{ message.time }}
                </div>
              </div>
            </div>
            
            <!-- Message Input -->
            <div class="chat-input">
              <div class="input-container">
                <textarea 
                  v-model="newMessage" 
                  @keydown.enter.prevent="sendMessage"
                  placeholder="Type your message..."
                  :disabled="!selectedModel"
                  rows="2"
                ></textarea>
                <button 
                  @click="sendMessage" 
                  :disabled="!selectedModel || !newMessage.trim()"
                  class="btn btn-primary send-btn"
                >
                  <i class="icon-send"></i>
                </button>
              </div>
              <div v-if="!selectedModel" class="input-hint">
                Please select a model to start chatting
              </div>
            </div>
          </div>
            </div>
            
            <!-- Tools Tab -->
            <div v-if="activeTab === 'tools'" class="tab-panel">
              <div class="tools-section">
                <div class="tools-header">
                  <h4>Tools</h4>
                  <div class="tools-filters">
                    <div class="search-box">
                      <i class="icon-search"></i>
                      <input v-model="toolsSearch" placeholder="Search tools...">
                    </div>
                    <select v-model="toolsCategory" class="filter-select">
                      <option value="">All Categories</option>
                      <option v-for="cat in catalogCategories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>
                  <div class="tools-actions" v-if="hasPendingToolChanges">
                    <button class="btn btn-secondary btn-sm" @click="revertToolChanges">Revert</button>
                    <button class="btn btn-primary btn-sm" @click="saveToolChanges">Save Changes</button>
                  </div>
                </div>

                <div v-if="toolsLoading" class="hint">Loading tools catalog…</div>
                <div v-else-if="toolsError" class="error">{{ toolsError }}</div>

                <div class="tools-subtitle" v-if="toolItems.length">Enabled</div>
                <div v-if="toolItems.length" class="tools-list">
                  <div 
                    v-for="tool in toolItems" 
                    :key="tool.key || tool.name"
                    class="tool-item"
                  >
                    <div class="tool-info">
                      <div class="tool-icon">🧰</div>
                      <div class="tool-meta">
                        <h5>{{ tool.name }}
                          <span v-if="tool.config?.verified === true" class="chip chip-ok">Verified</span>
                          <span v-else class="chip chip-warn">Needs setup</span>
                        </h5>
                        <p>{{ tool.description || 'No description' }}</p>
                      </div>
                    </div>

                    <div class="tool-controls">
                      <label class="switch">
                        <input type="checkbox" :checked="tool.enabled" @change="toggleTool(tool)">
                        <span class="slider"></span>
                      </label>
                      <button class="btn btn-outline btn-sm" @click="openToolConfig(tool)">Configure</button>
                      <button class="btn btn-outline btn-sm" @click="() => openToolTest(tool)">Test</button>
                    </div>
                  </div>
                </div>

                <div class="tools-subtitle">Available</div>
                <div v-if="availableTools.length" class="tools-list">
                  <div 
                    v-for="catTool in availableTools" 
                    :key="catTool.key || catTool.name"
                    class="tool-item available"
                  >
                    <div class="tool-info">
                      <div class="tool-icon">➕</div>
                      <div class="tool-meta">
                        <h5>{{ catTool.name }}</h5>
                        <p>{{ catTool.description || 'No description' }}</p>
                      </div>
                    </div>
                    <div class="tool-controls">
                      <button class="btn btn-primary btn-sm" @click="addToolFromCatalog(catTool)">Add</button>
                    </div>
                  </div>
                </div>
                <div v-else class="hint">No more tools to add.</div>

                <!-- Tool Test Modal -->
                <div v-if="showToolTest" class="tool-config-overlay" @click.self="closeToolTest">
                  <div class="tool-config-modal">
                    <div class="modal-header">
                      <h4>Test {{ currentTool?.name }}</h4>
                      <button class="btn-close" @click="closeToolTest"><i class="icon-close"></i></button>
                    </div>
                    <div class="modal-body">
                      <div class="form-group">
                        <label>Params (JSON)</label>
                        <textarea v-model="toolTestParams" rows="6" style="width:100%;"></textarea>
                      </div>
                      <div v-if="toolTestResult" class="test-result">
                        <pre>{{ toolTestResult }}</pre>
                      </div>
                      <div v-if="toolTestError" class="error">{{ toolTestError }}</div>
                    </div>
                    <div class="modal-footer">
                      <button class="btn btn-secondary" @click="closeToolTest">Close</button>
                      <button class="btn btn-primary" :disabled="toolTestLoading" @click="runToolTest">
                        <span v-if="toolTestLoading">Running…</span>
                        <span v-else>Run Test</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Simple configuration drawer/modal -->
                <div v-if="showToolConfig" class="tool-config-overlay" @click.self="closeToolConfig">
                  <div class="tool-config-modal">
                    <div class="modal-header">
                      <h4>Configure {{ currentTool?.name }}</h4>
                      <button class="btn-close" @click="closeToolConfig"><i class="icon-close"></i></button>
                    </div>
                    <div class="modal-body">
                      <!-- Zoho specific UI -->
                      <template v-if="isZohoTool(currentTool)">
                        <p class="config-help">Enter your Zoho OAuth credentials and choose services to link. We never display secrets after verification.</p>
                        <div class="form-row">
                          <div class="form-group">
                            <label>Client Id</label>
                            <input v-model="currentToolDraft.clientId" placeholder="Client Id">
                          </div>
                          <div class="form-group">
                            <label>Client Secret</label>
                            <input v-model="currentToolDraft.clientSecret" type="password" placeholder="Client Secret">
                          </div>
                        </div>
                        <div class="form-group">
                          <label>Enable Services</label>
                          <div class="checkbox-group">
                            <label class="checkbox-label"><input type="checkbox" v-model="currentToolDraft.useCrm"> <span>Zoho CRM</span></label>
                            <label class="checkbox-label"><input type="checkbox" v-model="currentToolDraft.useBooks"> <span>Zoho Books</span></label>
                          </div>
                        </div>
                        <div class="form-group" v-if="currentToolDraft.useBooks">
                          <label>Zoho Books Org Id</label>
                          <input v-model="currentToolDraft.zohoBookOrgId" placeholder="Organization Id">
                        </div>
                        <div class="hint" v-if="zohoMessage" :class="{ success: zohoSuccess }">{{ zohoMessage }}</div>
                      </template>

                      <!-- Generic config UI for non-Zoho tools -->
                      <template v-else>
                        <p class="config-help">This is a lightweight UI scaffold. Adjust settings below and save. Existing back-end contracts are not changed.</p>
                        <div class="form-group">
                          <label>Scope / Allowlist (comma-separated)</label>
                          <input v-model="currentToolDraft.scope" placeholder="example.com, api.uniphy.ai">
                        </div>
                        <div class="form-row">
                          <div class="form-group">
                            <label>Timeout (ms)</label>
                            <input type="number" v-model.number="currentToolDraft.timeout" min="1000" step="500">
                          </div>
                          <div class="form-group">
                            <label>Max Calls per Run</label>
                            <input type="number" v-model.number="currentToolDraft.maxCalls" min="1" step="1">
                          </div>
                        </div>
                      </template>
                    </div>
                    <div class="modal-footer">
                      <button class="btn btn-secondary" @click="closeToolConfig">Cancel</button>
                      <template v-if="isZohoTool(currentTool)">
                        <button class="btn btn-primary" :disabled="verifyZohoLoading || !(currentToolDraft.clientId && currentToolDraft.clientSecret && (currentToolDraft.useCrm || currentToolDraft.useBooks))" @click="verifyZohoTool">
                          <span v-if="verifyZohoLoading">Verifying…</span>
                          <span v-else>Verify with Zoho</span>
                        </button>
                      </template>
                      <template v-else>
                        <button class="btn btn-primary" @click="saveToolConfig">Save</button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
        
        <!-- Welcome Panel -->
        <div v-if="!selectedAgent && !showCreateForm && !editingAgent" class="welcome-panel">
          <div class="welcome-content">
            <i class="icon-agent-large"></i>
            <h3>Welcome to Agents</h3>
            <p>Select an agent from the left panel to view details and start chatting, or create a new agent to get started.</p>
            <button @click="showCreateForm = true" class="btn btn-primary">
              <i class="icon-plus"></i> Create Your First Agent
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import AgentMemoryManager from '@/components/AgentMemoryManager.vue'
import { getApiUrl } from '@/config/api'

export default {
  name: 'AgentsView',
  components: {
    AgentMemoryManager
  },
  setup() {
    const store = useStore()
    const agents = computed(() => {
      const rawAgents = store.state.agents?.items || []
      return Array.isArray(rawAgents) ? rawAgents.filter(agent => agent && typeof agent === 'object' && agent.id) : []
    })
    const models = computed(() => {
      const rawModels = store.state.llm?.items || []
      return Array.isArray(rawModels) ? rawModels.filter(model => model && typeof model === 'object' && model.id) : []
    })
    
    const selectedAgent = ref(null)
    const editingAgent = ref(null)
    const selectedModel = ref('')
    const newMessage = ref('')
    const agentSearch = ref('')
    const conversationSearch = ref('')

    const filteredAgents = computed(() => {
      const q = agentSearch.value.trim().toLowerCase()
      if (!q) return agents.value
      return agents.value.filter(a => (a.name || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q))
    })

    const filteredConversations = computed(() => {
      const q = conversationSearch.value.trim().toLowerCase()
      if (!q) return conversations.value
      return conversations.value.filter(c => {
        const title = formatConversationTitle(c).toLowerCase()
        const last = (c.last_message || '').toLowerCase()
        return title.includes(q) || last.includes(q)
      })
    })
    const error = ref('')
    const success = ref('')
    const chatHistory = ref([])

    // Tools state (non-breaking, local-first with optional save)
    const showToolConfig = ref(false)
    const currentTool = ref(null)
    const currentToolDraft = reactive({ scope: '', timeout: 10000, maxCalls: 3 })
    const toolStates = ref([]) // local editable list

    // Catalog and filters
    const toolsCatalog = ref([])
    const toolsLoading = ref(false)
    const toolsError = ref('')
    const toolsSearch = ref('')
    const toolsCategory = ref('')

    const toolItems = computed(() => {
      const tools = toolStates.value
      return Array.isArray(tools) ? tools : []
    })

    const hasPendingToolChanges = computed(() => {
      if (!selectedAgent.value) return false
      const original = normalizeTools(selectedAgent.value.tools)
      const edited = normalizeTools(toolStates.value)
      return JSON.stringify(original) !== JSON.stringify(edited)
    })

    const catalogCategories = computed(() => {
      const set = new Set((toolsCatalog.value || []).map(t => t.category).filter(Boolean))
      return Array.from(set)
    })

    const enabledKeys = computed(() => new Set((toolItems.value || []).map(t => String(t.key || t.name))))

    const availableTools = computed(() => {
      let list = (toolsCatalog.value || []).filter(c => !enabledKeys.value.has(String(c.key || c.name)))
      if (toolsCategory.value) list = list.filter(c => c.category === toolsCategory.value)
      const q = toolsSearch.value.trim().toLowerCase()
      if (q) list = list.filter(c => (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q))
      return list
    })
    const showCreateForm = ref(false)
    const chatMessages = ref(null)
    const session = ref('')  // Add session tracking
    const activeTab = ref('conversations')  // Tab management - default to conversations
    
    // Conversation-related data
    const conversations = ref([])
    const selectedConversation = ref(null)
    const conversationMessages = ref([])
    const conversationsLoading = ref(false)
    const conversationMessagesLoading = ref(false)
    
    // Form for creating/editing agents
    const agentForm = reactive({
      name: '',
      description: '',
      type: 'chat',
      llm_model_id: '',
      system_prompt: '',
      settings: {
        max_tokens: 1000,
        temperature: 0.7,
        top_k: 5,
        similarity_threshold: 0.7,
        enable_memory: true,
        max_memory_size: 10
      }
    })

    const fetchAgents = async () => {
      try {
        await store.dispatch('agents/fetchAll')
        await store.dispatch('llm/fetchAll')
      } catch (err) {
        console.error('Error fetching agents:', err)
        error.value = 'Failed to fetch agents'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const selectAgent = (agent) => {
      selectedAgent.value = agent
      editingAgent.value = null
      showCreateForm.value = false
      
      // Reset conversation state
      selectedConversation.value = null
      conversationMessages.value = []
      
      // Fetch conversations for this agent
      fetchConversations()
      
      // Initialize tools local state
      toolStates.value = normalizeTools(agent.tools)

      // Add sample chat history for demonstration
      chatHistory.value = [
        {
          type: 'agent',
          text: `Hello! I'm ${agent.name}. How can I help you today?`,
          time: new Date().toLocaleTimeString()
        },
        {
          type: 'user',
          text: 'Can you tell me about your capabilities?',
          time: new Date().toLocaleTimeString()
        },
        {
          type: 'agent',
          text: `I'm an AI agent specialized in ${agent.type} tasks. ${agent.description} I can help you with various tasks based on my configuration.`,
          time: new Date().toLocaleTimeString()
        }
      ]
      selectedModel.value = agent.llm_model_id || ''
      newMessage.value = ''
      // Scroll to bottom after loading chat
      setTimeout(() => scrollToBottom(), 100)
    }
    
    const editAgent = (agent) => {
      editingAgent.value = agent
      showCreateForm.value = false
      
      // Populate form with agent data
      agentForm.name = agent.name
      agentForm.description = agent.description
      agentForm.type = agent.type || 'chat'
      agentForm.llm_model_id = agent.llm_model_id
      agentForm.system_prompt = agent.system_prompt || ''
      
      // Copy settings or use defaults
      if (agent.settings) {
        agentForm.settings = { ...agent.settings }
      } else {
        agentForm.settings = {
          max_tokens: 1000,
          temperature: 0.7,
          top_k: 5,
          similarity_threshold: 0.7,
          enable_memory: true,
          max_memory_size: 10
        }
      }
    }
    
    const resetForm = () => {
      agentForm.name = ''
      agentForm.description = ''
      agentForm.type = 'chat'
      agentForm.llm_model_id = ''
      agentForm.system_prompt = ''
      agentForm.settings = {
        max_tokens: 1000,
        temperature: 0.7,
        top_k: 5,
        similarity_threshold: 0.7,
        enable_memory: true,
        max_memory_size: 10
      }
    }
    
    const closeForm = () => {
      showCreateForm.value = false
      editingAgent.value = null
      resetForm()
    }

    const createAgent = async () => {
      try {
        const result = await store.dispatch('agents/create', {
          name: agentForm.name,
          description: agentForm.description,
          type: agentForm.type,
          llm_model_id: agentForm.llm_model_id,
          system_prompt: agentForm.system_prompt,
          settings: agentForm.settings
        })
        
        success.value = 'Agent created successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchAgents()
        resetForm()
        showCreateForm.value = false
        
        // Select the newly created agent
        const newAgent = agents.value.find(a => a.id === result.id)
        if (newAgent) {
          selectAgent(newAgent)
        }
      } catch (err) {
        console.error('Error creating agent:', err)
        error.value = 'Failed to create agent'
        setTimeout(() => error.value = '', 3000)
      }
    }
    
    const updateAgent = async () => {
      if (!editingAgent.value) return
      
      try {
        await store.dispatch('agents/update', {
          id: editingAgent.value.id,
          updates: {
            name: agentForm.name,
            description: agentForm.description,
            type: agentForm.type,
            llm_model_id: agentForm.llm_model_id,
            system_prompt: agentForm.system_prompt,
            settings: agentForm.settings
          }
        })
        
        success.value = 'Agent updated successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchAgents()
        editingAgent.value = null
        
        // Refresh the selected agent
        const updatedAgent = agents.value.find(a => a.id === selectedAgent.value?.id)
        if (updatedAgent) {
          selectAgent(updatedAgent)
        }
      } catch (err) {
        console.error('Error updating agent:', err)
        error.value = 'Failed to update agent'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const sendMessage = async () => {
      if (!selectedModel.value || !newMessage.value.trim() || !selectedAgent.value) return

      const message = newMessage.value.trim()
      console.log('Sending message:', {
        message,
        modelId: selectedModel.value,
        agentId: selectedAgent.value.id,
        sessionId: session.value
      })
      // Add user message to chat
      chatHistory.value.push({
        type: 'user',
        text: message,
        time: new Date().toLocaleTimeString()
      })

      newMessage.value = ''
      scrollToBottom()

      try {
        // Add loading indicator
        chatHistory.value.push({
          type: 'agent',
          text: 'Thinking...',
          time: new Date().toLocaleTimeString(),
          isLoading: true
        })
        scrollToBottom()

        const response = await store.dispatch('agents/chat', {
          message,
          modelId: selectedModel.value,
          agentId: selectedAgent.value.id,
          sessionId: session.value
        })
        
        console.log('Agent response:', response)
        
        // Validate response structure
        if (!response || typeof response !== 'object') {
          throw new Error('Invalid response from server')
        }
        
        // Store session ID for future messages (using standardized property name)
        if (response.sessionId) {
          session.value = response.sessionId
        }

        // Remove loading message and add response
        chatHistory.value.pop()
        chatHistory.value.push({
          type: 'agent',
          text: response.message || 'Sorry, I could not process your request.',
          time: new Date().toLocaleTimeString()
        })
        scrollToBottom()
      } catch (err) {
        console.error('Chat error:', err)
        // Remove loading message
        chatHistory.value.pop()
        chatHistory.value.push({
          type: 'agent',
          text: 'Sorry, I encountered an error. Please try again.',
          time: new Date().toLocaleTimeString(),
          isError: true
        })
        scrollToBottom()
        error.value = 'Failed to send message'
        setTimeout(() => error.value = '', 3000)
      }
    }
    
    const scrollToBottom = async () => {
      await nextTick()
      if (chatMessages.value) {
        chatMessages.value.scrollTop = chatMessages.value.scrollHeight
      }
    }
    
    const getModelName = (modelId) => {
      const model = models.value.find(m => m.id === modelId)
      return model ? `${model.name} (${model.provider})` : 'Unknown Model'
    }

    // Enhanced conversation management functions with new endpoints
    const showEmptyConversations = ref(false)
    const conversationStats = ref(null)
    
    const fetchConversations = async () => {
      if (!selectedAgent.value) return
      
      conversationsLoading.value = true
      try {
        // Use new optimized endpoint with proper namespacing
        const response = await store.dispatch('agents/fetchConversations', {
          agentId: selectedAgent.value.id,
          includeEmpty: showEmptyConversations.value
        })
        conversations.value = response || []
        
        // Also fetch diagnostic info for better UX
        try {
          conversationStats.value = await store.dispatch('agents/diagnoseConversations', selectedAgent.value.id)
        } catch (diagError) {
          console.warn('Could not fetch conversation diagnostics:', diagError)
        }
      } catch (err) {
        console.error('Error fetching conversations:', err)
        error.value = 'Failed to fetch conversations'
        setTimeout(() => error.value = '', 3000)
      } finally {
        conversationsLoading.value = false
      }
    }
    
    const toggleEmptyConversations = async () => {
      showEmptyConversations.value = !showEmptyConversations.value
      await fetchConversations()
    }

    const selectConversation = async (conversation) => {
      selectedConversation.value = conversation
      conversationMessagesLoading.value = true
      
      try {
        // Use new endpoint that returns full session with messages
        const response = await store.dispatch('agents/getSession', conversation.id)
        conversationMessages.value = response?.messages || []
      } catch (err) {
        console.error('Error fetching conversation messages:', err)
        error.value = 'Failed to fetch conversation messages'
        setTimeout(() => error.value = '', 3000)
      } finally {
        conversationMessagesLoading.value = false
      }
    }

    const refreshConversations = async () => {
      await fetchConversations()
      selectedConversation.value = null
      conversationMessages.value = []
    }

    const exportConversation = () => {
      if (!selectedConversation.value || !conversationMessages.value.length) return
      
      const exportData = {
        conversation: selectedConversation.value,
        messages: conversationMessages.value,
        exportedAt: new Date().toISOString()
      }
      
      const dataStr = JSON.stringify(exportData, null, 2)
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
      
      const exportFileDefaultName = `conversation_${selectedConversation.value.id}_${new Date().toISOString().split('T')[0]}.json`
      
      const linkElement = document.createElement('a')
      linkElement.setAttribute('href', dataUri)
      linkElement.setAttribute('download', exportFileDefaultName)
      linkElement.click()
    }

    const deleteConversation = async () => {
      if (!selectedConversation.value) return
      
      if (!confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
        return
      }
      
      try {
        await store.dispatch('agents/deleteSession', {
          sessionId: selectedConversation.value.id
        })
        
        success.value = 'Conversation deleted successfully'
        setTimeout(() => success.value = '', 3000)
        
        await fetchConversations()
        selectedConversation.value = null
        conversationMessages.value = []
      } catch (err) {
        console.error('Error deleting conversation:', err)
        error.value = 'Failed to delete conversation'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const formatConversationTitle = (conversation) => {
      if (conversation.title) return conversation.title
      if (conversation.first_message) {
        return conversation.first_message.length > 50 
          ? conversation.first_message.substring(0, 50) + '...' 
          : conversation.first_message
      }
      return `Conversation ${conversation.id}`
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return 'Unknown'
      const date = new Date(dateString)
      return date.toLocaleString()
    }

    // Memory management functions
    const onMemoryUploadSuccess = (message) => {
      success.value = message
      setTimeout(() => success.value = '', 3000)
    }

    const onMemoryError = (message) => {
      error.value = message
      setTimeout(() => error.value = '', 3000)
    }


    onMounted(async () => {
      await fetchAgents()
      fetchToolsCatalog()
    })

    // Helper functions for tools
    const normalizeTools = (tools) => {
      if (!Array.isArray(tools)) return []
      return tools.map((t, idx) => {
        if (typeof t === 'string') return { key: t, name: t, description: '', enabled: true, order: idx }
        const key = t.key || t.name || `tool-${idx}`
        return {
          key,
          name: t.name || key,
          description: t.description || t.desc || '',
          enabled: !!(t.enabled ?? true),
          config: t.config || {}
        }
      })
    }

    const toggleTool = (tool) => {
      tool.enabled = !tool.enabled
    }

    const fetchToolsCatalog = async () => {
      if (toolsLoading.value || toolsCatalog.value.length) return
      toolsLoading.value = true
      toolsError.value = ''
      try {
        const token = localStorage.getItem('token')
        const resp = await fetch(getApiUrl('/api/tools/catalog'), {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        })
        if (!resp.ok) throw new Error('Failed to load tools catalog')
        const data = await resp.json()
        toolsCatalog.value = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : [])
      } catch (e) {
        console.error('Catalog error:', e)
        toolsError.value = e?.message || 'Failed to load tools catalog'
      } finally {
        toolsLoading.value = false
      }
    }

    const addToolFromCatalog = (catalogItem) => {
      const key = catalogItem.key || catalogItem.name
      if (!key) return
      const exists = toolStates.value.find(t => (t.key || t.name) === key)
      if (exists) return
      toolStates.value.push({
        key,
        name: catalogItem.name || key,
        description: catalogItem.description || '',
        enabled: true,
        config: catalogItem.defaultConfig || {}
      })
    }

    const openToolConfig = (tool) => {
      currentTool.value = tool
      // Generic defaults
      currentToolDraft.scope = (tool.config?.scope || []).join(', ')
      currentToolDraft.timeout = tool.config?.timeout || 10000
      currentToolDraft.maxCalls = tool.config?.maxCalls || 3

      // Zoho-specific prefill (only if verified not explicitly false)
      if (isZohoTool(tool)) {
        const cfg = tool.config || {}
        const keyStr = String(tool.key || tool.name || '').toLowerCase()
        if (cfg.verified !== false) {
          currentToolDraft.clientId = cfg.client_id || ''
          currentToolDraft.clientSecret = '' // never prefill secrets
          currentToolDraft.zohoBookOrgId = cfg.zoho_book_org_id || ''
          currentToolDraft.useBooks = keyStr.includes('zoho_books') || !!cfg.zoho_book_org_id
          currentToolDraft.useCrm = keyStr.includes('zoho_crm') || !!cfg.crm
        } else {
          currentToolDraft.clientId = ''
          currentToolDraft.clientSecret = ''
          currentToolDraft.zohoBookOrgId = ''
          currentToolDraft.useBooks = keyStr.includes('zoho_books')
          currentToolDraft.useCrm = keyStr.includes('zoho_crm')
        }
        zohoMessage.value = ''
        zohoSuccess.value = false
      }

      showToolConfig.value = true
    }

    const closeToolConfig = () => {
      showToolConfig.value = false
      currentTool.value = null
    }

    const saveToolConfig = () => {
      if (!currentTool.value) return
      if (isZohoTool(currentTool.value)) {
        // Do not persist secrets locally; Zoho config is verified through OAuth flow
        showToolConfig.value = false
        return
      }
      const scope = (currentToolDraft.scope || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      currentTool.value.config = {
        ...(currentTool.value.config || {}),
        scope,
        timeout: currentToolDraft.timeout,
        maxCalls: currentToolDraft.maxCalls
      }
      showToolConfig.value = false
    }

    const revertToolChanges = () => {
      if (!selectedAgent.value) return
      toolStates.value = normalizeTools(selectedAgent.value.tools)
    }

    const saveToolChanges = async () => {
      if (!selectedAgent.value) return
      try {
        const updates = { tools: toolStates.value }
        await store.dispatch('agents/update', { id: selectedAgent.value.id, updates })
        success.value = 'Tools updated successfully'
        setTimeout(() => success.value = '', 3000)
        // refresh agent
        await fetchAgents()
        const updated = agents.value.find(a => a.id === selectedAgent.value.id)
        if (updated) selectAgent(updated)
      } catch (err) {
        console.error('Failed to save tools', err)
        error.value = 'Failed to save tools'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const toggleAgentStatus = async (e) => {
      if (!selectedAgent.value) return
      const newStatus = e.target.checked
      try {
        await store.dispatch('agents/update', { id: selectedAgent.value.id, updates: { is_active: newStatus } })
        success.value = `Agent ${newStatus ? 'activated' : 'deactivated'}`
        setTimeout(() => success.value = '', 2500)
        await fetchAgents()
        const updated = agents.value.find(a => a.id === selectedAgent.value.id)
        if (updated) selectAgent(updated)
      } catch (err) {
        console.error('Failed to toggle status', err)
        error.value = 'Failed to update status'
        setTimeout(() => error.value = '', 3000)
      }
    }

    // Helpers
    const isZohoTool = (t) => {
      if (!t) return false
      const k = String(t.key || t.name || '').toLowerCase()
      return k.includes('zoho')
    }

    // Zoho verification inside Tools tab
    const zohoMessage = ref('')
    const zohoSuccess = ref(false)
    const verifyZohoLoading = ref(false)

    // Test harness state
    const showToolTest = ref(false)
    const toolTestParams = ref('')
    const toolTestLoading = ref(false)
    const toolTestResult = ref('')
    const toolTestError = ref('')

    const openToolTest = (tool) => {
      currentTool.value = tool
      showToolTest.value = true
      toolTestParams.value = '{\n  "example": true\n}'
      toolTestError.value = ''
      toolTestResult.value = ''
    }

    const closeToolTest = () => {
      showToolTest.value = false
      toolTestParams.value = ''
      toolTestError.value = ''
      toolTestResult.value = ''
    }

    const runToolTest = async () => {
      if (!currentTool.value || !selectedAgent.value) return
      toolTestLoading.value = true
      toolTestError.value = ''
      toolTestResult.value = ''
      try {
        const token = localStorage.getItem('token')
        let params
        try { params = toolTestParams.value ? JSON.parse(toolTestParams.value) : {} } catch (e) { throw new Error('Params must be valid JSON') }
        const resp = await fetch(getApiUrl('/api/tools/test'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ toolKey: currentTool.value.key || currentTool.value.name, agentId: selectedAgent.value.id, params })
        })
        if (!resp.ok) {
          let errText = 'Tool test failed'
          try { const err = await resp.json(); errText = err.error || err.message || errText } catch {}
          throw new Error(errText)
        }
        const data = await resp.json()
        toolTestResult.value = JSON.stringify(data, null, 2)
      } catch (e) {
        toolTestError.value = e?.message || 'Test failed'
      } finally {
        toolTestLoading.value = false
      }
    }

    const verifyZohoTool = async () => {
      if (!currentTool.value || !isZohoTool(currentTool.value) || !selectedAgent.value) return
      zohoMessage.value = ''
      zohoSuccess.value = false
      verifyZohoLoading.value = true
      try {
        const token = localStorage.getItem('token')
        // Determine tool type based on the selected tool key/name
        const keyStr = String(currentTool.value.key || currentTool.value.name || '').toLowerCase()
        let toolType = 'zoho'
        if (keyStr.includes('zoho_books')) toolType = 'zoho_books'
        else if (keyStr.includes('zoho_crm')) toolType = 'zoho_crm'

        // Include agent_id in redirect URI so we can recover it on callback
        const redirectUri = `${window.location.origin}/callback?agent_id=${encodeURIComponent(selectedAgent.value.id)}`
        const payload = {
          grant_type: 'authorization_code',
          client_id: currentToolDraft.clientId || '',
          client_secret: currentToolDraft.clientSecret || '',
          redirect_uri: redirectUri,
          tool_type: toolType
        }
        if (currentToolDraft.useBooks) {
          payload.zoho_book_org_id = currentToolDraft.zohoBookOrgId || ''
        }

        const resp = await fetch(getApiUrl(`/api/agents/${selectedAgent.value.id}/tools/zoho`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(payload)
        })
        if (!resp.ok) {
          let errText = 'Verification failed.'
          try {
            const err = await resp.json()
            errText = err.error || err.message || errText
          } catch {}
          throw new Error(errText)
        }

        // Build scopes
        const scopes = []
        if (currentToolDraft.useCrm) scopes.push('ZohoCRM.modules.ALL', 'ZohoCRM.settings.ALL')
        if (currentToolDraft.useBooks) scopes.push('ZohoBooks.fullaccess.all', 'ZohoBooks.invoices.READ', 'ZohoBooks.invoices.CREATE')
        const scopeParam = scopes.join(',')

        const authUrl = new URL('https://accounts.zoho.com/oauth/v2/auth')
        authUrl.searchParams.set('scope', scopeParam)
        authUrl.searchParams.set('client_id', currentToolDraft.clientId || '')
        authUrl.searchParams.set('response_type', 'code')
        authUrl.searchParams.set('access_type', 'offline')
        authUrl.searchParams.set('redirect_uri', redirectUri)
        // Use state to signal which Zoho tool is being authorized
        authUrl.searchParams.set('state', toolType)

        zohoSuccess.value = true
        zohoMessage.value = 'Redirecting to Zoho for authorization…'
        window.location.assign(authUrl.toString())
      } catch (e) {
        console.error('Zoho verify error:', e)
        zohoSuccess.value = false
        zohoMessage.value = e?.message || 'Verification failed.'
      } finally {
        verifyZohoLoading.value = false
      }
    }

    return {
      agents,
      models,
      filteredAgents,
      agentSearch,
      conversationSearch,
      filteredConversations,
      selectedAgent,
      editingAgent,
      selectedModel,
      newMessage,
      chatHistory,
      chatMessages,
      error,
      success,
      showCreateForm,
      agentForm,
      activeTab,
      conversations,
      selectedConversation,
      conversationMessages,
      conversationsLoading,
      conversationMessagesLoading,
      selectAgent,
      editAgent,
      closeForm,
      createAgent,
      updateAgent,
      sendMessage,
      getModelName,
      fetchConversations,
      selectConversation,
      refreshConversations,
      exportConversation,
      deleteConversation,
      formatConversationTitle,
      formatDateTime,
      onMemoryUploadSuccess,
      onMemoryError,
      // tools bindings
      toolItems,
      showToolConfig,
      currentTool,
      currentToolDraft,
      hasPendingToolChanges,
      toggleTool,
      openToolConfig,
      closeToolConfig,
      saveToolConfig,
      saveToolChanges,
      revertToolChanges,
      toggleAgentStatus,
      // zoho helpers in modal
      isZohoTool,
      verifyZohoTool,
      verifyZohoLoading,
      zohoMessage,
      zohoSuccess,
      // test harness
      showToolTest,
      toolTestParams,
      toolTestLoading,
      toolTestResult,
      toolTestError,
      openToolTest,
      closeToolTest,
      runToolTest,
      // catalog
      toolsCatalog,
      toolsLoading,
      toolsError,
      toolsSearch,
      toolsCategory,
      catalogCategories,
      availableTools,
      addToolFromCatalog,
      fetchToolsCatalog
    }
  }
}
</script>

<style scoped>
/* Main Container */
.agents-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #ffffff;
  color: #2c3e50;
  border-bottom: 1px solid #e9ecef;
}

.page-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

/* Messages */
.error-message {
  background: linear-gradient(45deg, #ff6b6b, #ee5a52);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

.success-message {
  background: linear-gradient(45deg, #51cf66, #40c057);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(81, 207, 102, 0.3);
}

/* Main Content Layout */
.main-content {
  display: flex;
  flex: 1;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  overflow: hidden;
}

/* Left Panel - Agents List */
.left-panel {
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.agent-count {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h4 {
  margin: 1rem 0 0.5rem;
  color: #495057;
}

/* Agents List */
.agents-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.list-toolbar { padding: 0.75rem 1rem; border-bottom: 1px solid #e9ecef; background: #fff; }
.list-toolbar .search-box { position: relative; }
.list-toolbar .search-box i { position: absolute; left: 0.5rem; top: 50%; transform: translateY(-50%); color: #6c757d; }
.list-toolbar .search-box input { width: 100%; padding: 0.5rem 0.75rem 0.5rem 2rem; border: 1px solid #e9ecef; border-radius: 16px; font-size: 0.85rem; }

.agent-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.agent-item:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.agent-item.active {
  background: linear-gradient(45deg, #e3f2fd, #f3e5f5);
  border-color: #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.agent-info {
  flex: 1;
  min-width: 0;
}

.agent-info h4 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.agent-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.agent-type {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.agent-status {
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #ffebee;
  color: #c62828;
}

.agent-status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.agent-actions {
  opacity: 0;
  transition: opacity 0.2s;
}

.agent-item:hover .agent-actions {
  opacity: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e9ecef;
  color: #495057;
}

/* Right Panel */
.right-panel {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Agent Form Panel */
.agent-form-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.agent-form {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

input, textarea, select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.range-input {
  appearance: none;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  outline: none;
}

.range-input::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  background: #2196f3;
  border-radius: 50%;
  cursor: pointer;
}

.settings-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.settings-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 0.75rem;
  cursor: pointer;
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  background: white;
  transition: all 0.2s ease;
}

.checkbox-label input[type="checkbox"]:hover {
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.checkbox-label input[type="checkbox"]:checked {
  background: #2196f3;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.checkbox-label input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
}

.checkbox-label input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.2);
}

.form-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;
}

.btn-close:hover {
  color: #495057;
}

/* Agent Details Panel */
.agent-details-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.agent-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.agent-header .model-chip {
  background: #f0f4ff;
  color: #2b50a3;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

.agent-header .rag-chip {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

/* Switch */
.switch { position: relative; display: inline-block; width: 42px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #e9ecef; transition: .2s; border-radius: 24px; }
.slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .2s; border-radius: 50%; }
.switch input:checked + .slider { background: #667eea; }
.switch input:checked + .slider:before { transform: translateX(18px); }

.agent-type-badge {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.agent-overview {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.agent-description {
  margin: 0 0 1rem;
  color: #6c757d;
  line-height: 1.5;
}

.agent-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.detail-item .label {
  font-weight: 600;
  color: #495057;
}

.detail-item .value {
  color: #6c757d;
}

.detail-item .status {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  background: #ffebee;
  color: #c62828;
}

.detail-item .status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  border-bottom: 2px solid #e9ecef;
  background: #f8f9fa;
}

.tab-btn {
  flex: 1;
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-bottom: 3px solid transparent;
}

.tab-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  color: #495057;
}

.tab-btn.active {
  background: white;
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

.tab-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tab-panel {
  flex: 1;
  overflow-y: auto;
}

/* Overview Tab Styles */
.system-prompt-section {
  margin-top: 2rem;
}

.system-prompt-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
}

.system-prompt {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
  border-left: 4px solid #667eea;
}

.settings-overview {
  margin-top: 2rem;
}

.settings-overview h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.setting-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.setting-value {
  color: #667eea;
  font-weight: 500;
  font-size: 0.9rem;
}

/* Chat Section */
.chat-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  min-height: 0;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.chat-header h4 {
  margin: 0;
  color: #2c3e50;
}

.rag-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #e8f5e8;
  color: #2e7d32;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.model-selector {
  margin-bottom: 1rem;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  min-height: 200px;
  max-height: 400px;
  scroll-behavior: smooth;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
}

.no-messages i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.message {
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.message.user {
  align-items: flex-end;
}

.message.agent {
  align-items: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 0.75rem 1rem;
  border-radius: 18px;
  word-wrap: break-word;
}

.message.user .message-content {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.message.agent .message-content {
  background: white;
  color: #2c3e50;
  border: 1px solid #e9ecef;
}

.message-time {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

.chat-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-container {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
}

.input-container textarea {
  flex: 1;
  resize: none;
  border-radius: 20px;
  padding: 0.75rem 1rem;
}

.send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.input-hint {
  text-align: center;
  color: #6c757d;
  font-size: 0.85rem;
  padding: 0.5rem;
}

/* Tools Section */
.tools-section { padding: 1.5rem; }
.tools-header { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 0.75rem; }
.tools-filters { display: flex; align-items: center; gap: 0.5rem; justify-content: flex-start; }
.tools-subtitle { margin: 0.75rem 0 0.25rem; color: #495057; font-weight: 600; }
.tools-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.tools-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1rem; }
.tool-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; border: 1px solid #e9ecef; border-radius: 8px; background: #fff; }
.tool-info { display: flex; align-items: center; gap: 0.75rem; }
.tool-icon { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: #f8f9fa; }
.tool-meta h5 { margin: 0; font-size: 1rem; color: #2c3e50; display:flex; align-items:center; gap:0.5rem; }
.chip { display:inline-block; padding: 0.1rem 0.4rem; border-radius: 10px; font-size: 0.7rem; }
.chip-ok { background: #e8f5e8; color: #2e7d32; }
.chip-warn { background: #fff3cd; color: #856404; }
.tool-meta p { margin: 0; font-size: 0.85rem; color: #6c757d; }
.tool-controls { display: flex; align-items: center; gap: 0.5rem; }

/* Tool config modal */
.tool-config-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.tool-config-modal { width: 90%; max-width: 700px; background: #fff; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.2); overflow: hidden; }
.tool-config-modal .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e9ecef; }
.tool-config-modal .modal-body { padding: 1.25rem; }
.tool-config-modal .modal-body textarea { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
.tool-config-modal .config-help { color: #6c757d; font-size: 0.9rem; margin-top: -0.25rem; margin-bottom: 1rem; }
.tool-config-modal .modal-footer { padding: 1rem 1.25rem; border-top: 1px solid #e9ecef; display: flex; justify-content: flex-end; gap: 0.5rem; }

/* Welcome Panel */
.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.welcome-content {
  text-align: center;
  color: #6c757d;
  max-width: 400px;
}

.welcome-content i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
}

.welcome-content h3 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.welcome-content p {
  margin: 0 0 2rem;
  line-height: 1.6;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

.btn-outline {
  background: transparent;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-outline:hover {
  background: #f8f9fa;
  color: #495057;
}

/* Icons (you'll need to add an icon font or replace with actual icons) */
.icon-plus:before { content: '+'; }
.icon-agent:before { content: '🤖'; }
.icon-bot:before { content: '🤖'; }
.icon-edit:before { content: '✏️'; }
.icon-close:before { content: '✕'; }
.icon-agent-large:before { content: '🤖'; }
.icon-chat:before { content: '💬'; }
.icon-send:before { content: '→'; }
.icon-knowledge:before { content: '📚'; }
.icon-memory:before { content: '🧠'; }
.icon-info:before { content: 'ℹ️'; }
.icon-refresh:before { content: '🔄'; }
.icon-export:before { content: '📤'; }
.icon-trash:before { content: '🗑️'; }
.icon-loading:before { content: '⏳'; }

/* Conversation Styles */
.conversations-section {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}

.conversations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.convo-actions { display: flex; align-items: center; gap: 0.75rem; }

.search-box { position: relative; }
.search-box i { position: absolute; left: 0.5rem; top: 50%; transform: translateY(-50%); color: #6c757d; }
.search-box input { padding: 0.5rem 0.75rem 0.5rem 2rem; border: 1px solid #e9ecef; border-radius: 16px; font-size: 0.85rem; }

.conversations-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.conversations-list {
  margin-bottom: 2rem;
}

.conversation-item {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.conversation-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.conversation-item.active {
  border-color: #667eea;
  background: #f8f9ff;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.conversation-info h5 {
  margin: 0 0 0.25rem;
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 500;
}

.conversation-time {
  color: #6c757d;
  font-size: 0.8rem;
}

.conversation-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.message-count {
  font-size: 0.8rem;
  color: #6c757d;
}

.conversation-status {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.conversation-status.active {
  background: #d4edda;
  color: #155724;
}

.conversation-status.archived {
  background: #f8d7da;
  color: #721c24;
}

.conversation-preview p {
  margin: 0;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

.conversation-messages {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.messages-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.messages-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.message-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

.btn-danger {
  background: #dc3545;
  color: white;
  border: none;
}

.btn-danger:hover {
  background: #c82333;
}

.messages-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.message-item {
  padding: 1rem;
  border-bottom: 1px solid #f8f9fa;
}

.message-item:last-child {
  border-bottom: none;
}

.message-item.user {
  background: #f8f9ff;
}

.message-item.agent {
  background: white;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.message-role {
  font-weight: 500;
  color: #2c3e50;
  text-transform: capitalize;
}

.message-time {
  color: #6c757d;
  font-size: 0.8rem;
}

.message-content {
  color: #495057;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.message-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

.loading-state i, .empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.3;
}

.empty-state h4 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
}

.empty-state p {
  margin: 0;
  line-height: 1.6;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>

